// JOBE-0044.9: UserSavedViewsBlock — renders the user-saved views
// list below the built-in six. Each row carries a hover-revealed
// overflow trigger (three-dot glyph) that opens a small inline menu
// with Rename / Delete. Rename opens the shared SavedViewModal;
// Delete renders a confirm in-row.
//
// Built on the same RailRow primitive the built-in block uses, so
// the visual treatment is consistent. Status mirrors the rail's
// existing degrade rules — loading → skeleton row, empty → silent
// (the block renders nothing when the user has no saved views, so
// the rail doesn't grow real estate for an empty list).

import React, { useState } from 'react';
import { RailRow } from './RailRow.jsx';
import { SavedViewModal } from './SavedViewModal.jsx';

export function UserSavedViewsBlock({
  views,
  status,
  activeView,
  onPickView,
  onRename,
  onRemove,
}) {
  const [renaming, setRenaming] = useState(null); // SavedViewRow being renamed
  const [confirmingDelete, setConfirmingDelete] = useState(null); // slug
  const [error, setError] = useState(null);

  if (status === 'loading') {
    // Quiet — the built-in views render synchronously; a skeleton
    // here would create a visual stutter for the common case.
    return null;
  }
  if (status === 'error') {
    return (
      <div className="library-rail-block-error" role="alert">
        couldn't load saved views
      </div>
    );
  }
  if (!Array.isArray(views) || views.length === 0) {
    return null;
  }

  const onRenameSubmit = async ({ name }) => {
    if (!renaming) return;
    try {
      await onRename(renaming.slug, { name });
      setRenaming(null);
    } catch (err) {
      throw err; // modal renders the error
    }
  };

  const onDeleteConfirm = async (slug) => {
    setError(null);
    try {
      await onRemove(slug);
      setConfirmingDelete(null);
    } catch (err) {
      setError(err?.message || String(err));
    }
  };

  return (
    <ul className="library-rail-block-list user-saved-views-list">
      {views.map((view) => {
        const isConfirming = confirmingDelete === view.slug;
        return (
          <li key={view.urn} className="user-saved-views-row">
            <RailRow
              glyph="·"
              label={view.name || view.slug}
              title={view.description || view.name || ''}
              selected={activeView === view.slug}
              onClick={() => onPickView && onPickView(view)}
            />
            <UserSavedViewMenu
              onRename={() => setRenaming(view)}
              onDelete={() => setConfirmingDelete(view.slug)}
            />
            {isConfirming ? (
              <div className="user-saved-views-confirm" role="alertdialog">
                <span>Delete this view?</span>
                <button
                  type="button"
                  className="user-saved-views-confirm-delete"
                  onClick={() => onDeleteConfirm(view.slug)}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="user-saved-views-confirm-cancel"
                  onClick={() => setConfirmingDelete(null)}
                >
                  Cancel
                </button>
                {error ? (
                  <span className="user-saved-views-confirm-error">{error}</span>
                ) : null}
              </div>
            ) : null}
          </li>
        );
      })}
      {renaming ? (
        <SavedViewModal
          mode="rename"
          initialName={renaming.name}
          chips={parseQueryChips(renaming.query)}
          onSubmit={onRenameSubmit}
          onCancel={() => setRenaming(null)}
        />
      ) : null}
    </ul>
  );
}

function UserSavedViewMenu({ onRename, onDelete }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        type="button"
        className="user-saved-views-menu-trigger"
        aria-label="More actions"
        onClick={() => setOpen(true)}
      >
        ⋯
      </button>
    );
  }
  return (
    <div className="user-saved-views-menu" role="menu">
      <button
        type="button"
        className="user-saved-views-menu-item"
        role="menuitem"
        onClick={() => {
          setOpen(false);
          onRename();
        }}
      >
        Rename
      </button>
      <button
        type="button"
        className="user-saved-views-menu-item user-saved-views-menu-item-danger"
        role="menuitem"
        onClick={() => {
          setOpen(false);
          onDelete();
        }}
      >
        Delete
      </button>
      <button
        type="button"
        className="user-saved-views-menu-close"
        aria-label="Close menu"
        onClick={() => setOpen(false)}
      >
        ×
      </button>
    </div>
  );
}

function parseQueryChips(query) {
  if (!query || typeof query !== 'string') return [];
  try {
    const parsed = JSON.parse(query);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}
